import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { useLanguage, useTranslation } from '../i18n';
import { getMoodEmoji, getMoodColor } from '../data/mockData';
import './JournalListPage.css';

export const JournalListPage: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = useTranslation();
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getJournalEntries();
      setEntries(response.data);
    } catch (error) {
      console.error('Failed to load journal entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEntry = () => {
    navigate('/journal/add');
  };

  const getLocale = () => {
    switch (language) {
      
      
      default: return 'es-ES';
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return t.common.today;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return t.common.yesterday;
    } else {
      return date.toLocaleDateString(getLocale(), { day: 'numeric', month: 'long' });
    }
  };

  const getActivityLabel = (activity: string): string => {
    const labels: Record<string, string> = {
      meditation: t.journal.meditationActivity,
      work: t.journal.work,
      reading: t.journal.reading,
      yoga: t.journal.yoga,
      social: t.journal.social,
      walk: t.journal.walk
    };
    return labels[activity] || activity;
  };

  if (loading) {
    return (
      <div className="page-container journal-list-page">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>{t.journal.loadingEntries}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container journal-list-page">
      <div className="page-heading-section">
        <h1 className="page-title">{t.journal.title}</h1>
        <p className="page-subtitle">{t.journal.subtitle}</p>
      </div>

      <button className="add-entry-button" onClick={handleAddEntry}>
        <span className="add-icon">+</span>
        <span>{t.journal.addEntry}</span>
      </button>

      <div className="journal-entries">
        {entries.map((entry) => (
          <div key={entry.id} className="journal-entry-card">
            <div className="entry-header">
              <div 
                className="mood-indicator" 
                style={{ backgroundColor: getMoodColor(entry.mood) }}
              >
                <span className="mood-emoji">{getMoodEmoji(entry.mood)}</span>
              </div>
              <div className="entry-meta">
                <div className="entry-date">{formatDate(entry.date)}</div>
                <div className="entry-time">
                  {new Date(entry.date).toLocaleTimeString(getLocale(), { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </div>
              </div>
            </div>
            <div className="entry-content">
              <p className="entry-note">{entry.note}</p>
              {entry.activities && entry.activities.length > 0 && (
                <div className="entry-activities">
                  {entry.activities.map((activity: string, index: number) => (
                    <span key={index} className="activity-tag">
                      {getActivityLabel(activity)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {entries.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">📔</div>
          <h3>{t.journal.noEntries}</h3>
          <p>{t.journal.startJournal}</p>
        </div>
      )}
    </div>
  );
};
